// src/services/LoginService.ts
import apiClient from "../api/ApiClient";
import { fetchReservation } from "../api/ReservationService";
import { fetchCountries, fetchNationalities, fetchTitles } from "../api/StaticDataService";
import { AppAction } from "../context/AppContext";
import { mapGuestsFromReservation } from "../utils/GuestMapper";
import { getRoomTypeDescription } from "../utils/reservationUtils";
import { clearToken } from "./AuthService";
// If you have clearToken() in AuthService, you can import it and use it below.
// import { setToken, clearToken } from "./AuthService";

export const loginAndFetchInitialData = async (
  lastName: string,
  confirmationNumber: string,
  dispatch: React.Dispatch<AppAction>
): Promise<void> => {
  try {
      clearToken();

      // keep only session-scoped metadata
      sessionStorage.setItem("lastName", lastName);

      // 1) Reservation lookup is public; response includes check-in session JWT for all other /checkin calls.
      const reservation = await fetchReservation(lastName, confirmationNumber);

      if (!reservation.checkInAccessToken) {
          const err: any = new Error("MISSING_CHECKIN_SESSION_TOKEN");
          err.code = "MISSING_CHECKIN_SESSION_TOKEN";
          throw err;
      }

      // 2) LOVs require check-in session Bearer token
      const [titles, nationalities, countries] = await Promise.all([
        fetchTitles(),
        fetchNationalities(),
        fetchCountries(),
      ]);

      const confirmationForSession =
        (reservation.bookingConfirmationNumber?.trim() ||
          reservation.reservationId?.trim() ||
          confirmationNumber.trim());
      sessionStorage.setItem("confirmationNumber", confirmationForSession);

      // 3) TEMP FE GUARD:
      // If reservation comes back with 0 adults, simulate login failure.
      if (!reservation || reservation.adultCount === 0) {
        const err: any = new Error("RESERVATION_INVALID_OR_EMPTY");
        err.code = "RESERVATION_INVALID_OR_EMPTY";
        throw err;
      }

      const guests = await mapGuestsFromReservation(reservation);

      // 4) Dispatch raw LOV maps to state. The per-field router (getLovForField) owns the
      // "empty COUNTRYCODES -> substitute nationalities" fallback now, so the consumer (form
      // dropdown / validator / scanner) sees the right map for the chain's lovSources config
      // without us having to mutate either map here.
      dispatch({ type: "SET_TITLES", payload: titles });
      dispatch({ type: "SET_NATIONALITIES", payload: nationalities });
      dispatch({ type: "SET_COUNTRIES", payload: countries });
      dispatch({
        type: "SET_STATE",
        payload: {
          reservationId: reservation.reservationId,
          hotel: reservation.hotelName,
          hotelId: reservation.hotelId,
          arrivalDate: reservation.arrivalDate,
          departureDate: reservation.departureDate,
          adultCount: reservation.adultCount,
          childCount: reservation.childCount,
          roomType: getRoomTypeDescription(reservation),
          user: guests[0],
          mainGuestId: reservation.mainGuest?.id ?? null,
          guests,
        },
      });
  } catch (err) {
      // Cleanup so the UI looks like login failed
      try {
        sessionStorage.removeItem("lastName");
        sessionStorage.removeItem("confirmationNumber");
        clearToken();

        // Fallback: remove Authorization header for the current axios client
        if (apiClient?.defaults?.headers?.common) {
          delete apiClient.defaults.headers.common["Authorization"];
        }
      } catch {
        // no-op
      }

      // Re-throw so the page shows the generic "Authentication failed..." message
      throw err;
  }
};
